import React from "react";
import { twMerge } from "tailwind-merge";

const ICON_COLOR = {
  error600: "text-error600",
  gray600: "text-gray600",
  primary600: "text-primary600",
  success600: "text-success600",
  warning600: "text-warning600",
  white: "text-white",
};

const INNER_COLOR = {
  error100: "bg-error100",
  gray100: "bg-gray100",
  primary100: "bg-primary100",
  success100: "bg-success100",
  warning100: "bg-warning100",
  white: "bg-white",
};

const OUTER_COLOR = {
  error50: "bg-error50",
  gray50: "bg-gray50",
  primary50: "bg-primary50",
  success50: "bg-success50",
  warning50: "bg-warning50",
  white: "bg-white",
};

const SIZE = {
  sm: {
    base: "size-8 min-w-8",
    inner: "size-6 min-w-6",
    icon: "size-4 min-w-4",
  },
  md: {
    base: "size-10 min-w-10",
    inner: "size-8 min-w-8",
    icon: "size-5 min-w-5",
  },
  lg: {
    base: "size-12 min-w-12",
    inner: "size-10 min-w-10",
    icon: "size-6 min-w-6",
  },
  xl: {
    base: "size-14 min-w-14",
    inner: "size-10 min-w-10",
    icon: "size-7 min-w-7",
  },
};

export default function IconBadge({
  as,
  className,
  disabled,
  href,
  icon: Icon,
  iconNode,
  innerClassName,
  onClick,

  // variants (old repo friendly)
  iconColor = "gray600",
  innerColor = "gray100",
  outerColor = "gray50",
  size = "md",

  // a11y
  "aria-label": ariaLabel,
}) {
  const isClickable = Boolean(onClick || href);
  const Comp = as || (href ? "a" : isClickable ? "button" : "div");

  const baseSize = (SIZE[size] || SIZE.md).base;
  const innerSize = (SIZE[size] || SIZE.md).inner;
  const iconSize = (SIZE[size] || SIZE.md).icon;

  const baseClasses = twMerge(
    "inline-flex items-center justify-center rounded-full transition-transform duration-200",
    OUTER_COLOR[outerColor] || OUTER_COLOR.gray50,
    baseSize,
    isClickable && !disabled
      ? "cursor-pointer active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary300"
      : "",
    disabled ? "pointer-events-none opacity-50" : "",
    className
  );

  const innerClasses = twMerge(
    "flex items-center justify-center rounded-full",
    INNER_COLOR[innerColor] || INNER_COLOR.gray100,
    innerSize,
    innerClassName
  );

  const iconClasses = twMerge(
    ICON_COLOR[iconColor] || ICON_COLOR.gray600,
    iconSize
  );

  const handleClick = disabled ? undefined : onClick;

  return (
    <Comp
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      className={baseClasses}
      href={Comp === "a" ? href : undefined}
      onClick={handleClick}
      type={Comp === "button" ? "button" : undefined}
    >
      <span className={innerClasses}>
        {iconNode || (Icon ? <Icon className={iconClasses} /> : null)}
      </span>
    </Comp>
  );
}
